import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Flag } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (!r.ok) setError(r.error);
    else navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-flag-ivory flex flex-col">
      <div className="stripe-accent" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 justify-center font-display font-black text-2xl text-flag-navy mb-6" data-testid="login-logo">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-flag-red text-white">
              <Flag className="h-4 w-4" />
            </span>
            Electable
          </Link>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <h1 className="font-display text-2xl font-bold text-flag-navy">Welcome back</h1>
            <p className="mt-1 text-slate-600">Sign in to your campaign.</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-800">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="mt-1 h-12 text-base"
                  placeholder="you@campaign.com"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-slate-800">Password</Label>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="mt-1 h-12 text-base"
                  placeholder="••••••••"
                />
                <div className="mt-2 text-right">
                  <Link to="/forgot-password" className="text-xs font-semibold text-flag-navy hover:text-flag-red hover:underline" data-testid="login-forgot-link">
                    Forgot password?
                  </Link>
                </div>
              </div>
              {error && (
                <div className="text-sm text-flag-red bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold text-base"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-slate-600 text-center">
              New here?{" "}
              <Link to="/register" className="font-semibold text-flag-navy hover:underline" data-testid="login-register-link">
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
